import React, { useEffect, useRef } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { getProjectsForLocale } from '../data/projects';
import { categories } from '../data/products';
import { ArrowRight, CheckCircle2, ChevronRight, HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLocale } from '../utils/i18n';
export function Project() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    locale
  } = useLocale();
  // Locale-aware project list — slugs are stable across EN/ES, so the
  // URL still resolves correctly when the user switches languages mid-page.
  const projects = getProjectsForLocale(locale);
  const project = projects.find(p => p.slug === slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  // Mobile-only ordering: active project pinned to position 0 so it's
  // always the first thing the user sees in the horizontal scroller —
  // no need to scroll to find "you are here". The sm+ grid keeps the
  // canonical order for desktop browsing.
  const activeIndex = project ? projects.findIndex(p => p.slug === project.slug) : -1;
  const mobileProjects = activeIndex >= 0 ? [...projects.slice(activeIndex), ...projects.slice(0, activeIndex)] : projects;
  // Reset the mobile scroller back to the start whenever the active
  // project changes so the freshly-promoted "first" pill is actually
  // visible — otherwise the previous scrollLeft value persists and the
  // user sees the pills already partially scrolled.
  const mobileScrollerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (mobileScrollerRef.current) {
      mobileScrollerRef.current.scrollLeft = 0;
    }
  }, [slug]);
  // SEO — set page title to the project name + intent + brand. Mirrors the
  // pattern used in pages/CityMaterial.tsx so HashRouter pages all have
  // crawlable titles. Also sets a meta description from the project tagline
  // for search-result snippets.
  useEffect(() => {
    if (!project) return;
    const originalTitle = document.title;
    document.title = `${project.name} — Materials & Pro Tips | 253Gravel`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute('content') ?? '';
    if (metaDesc) {
      metaDesc.setAttribute('content', `${project.tagline} See the recommended materials, depths, and pro tips for a ${project.name.toLowerCase()} project in the Tacoma area.`);
    }
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute('content', originalDesc);
    };
  }, [project]);
  if (!project) {
    return <Navigate to="/" replace data-id="element-3087" />;
  }
  const Icon = project.icon;
  // Helper to find full product details from data/products.ts
  const getProductDetails = (productSlug: string) => {
    for (const cat of categories) {
      const product = cat.products.find(p => p.slug === productSlug);
      if (product) {
        return {
          ...product,
          categoryName: cat.name
        };
      }
    }
    return null;
  };
  return <main className="min-h-screen bg-[#fdfbf7] dark:bg-[#222222] pt-8 pb-24" data-id="element-3088">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3089">
        {/* Breadcrumbs */}
        <nav className="flex items-center text-sm text-stone-500 dark:text-stone-400 mb-6" aria-label="Breadcrumb" data-id="element-3090">
          <Link to="/" className="hover:text-brand-orange transition-colors" data-id="element-3091">
            Home
          </Link>
          <ChevronRight className="w-4 h-4 mx-2" data-id="element-3092" />
          <span className="text-stone-900 dark:text-white font-medium" data-id="element-3093">
            {project.name}
          </span>
        </nav>

        {/* Project switcher — bold card-style tab grid. Each tab shows
            the project icon prominently with the project name as the
            primary label and typical yards as a quiet subtitle. Active
            project gets a saturated sage fill so the current context is
            unmissable; inactive tabs have a clear hover state to invite
            switching. 2 columns on mobile, 3 on sm+, 4 on md+. */}
        <div className="mb-10" data-id="element-3094">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400 mb-3" data-id="element-3095">
            Shop by Project
          </h2>
          {/* Mobile: horizontal scrollable pill row so the project content
           stays visible below the tabs (no long vertical menu to scroll past).
           sm+: switches to a multi-column grid for desktop browsing. */}
          <div ref={mobileScrollerRef} className="flex sm:hidden gap-2 overflow-x-auto pb-2 snap-x snap-mandatory" style={{
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }} data-id="element-3096">
            <style data-id="element-3097">{`
              .project-tabs-mobile::-webkit-scrollbar { display: none; }
            `}</style>
            {mobileProjects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border transition-all snap-start ${isActive ? 'bg-[#059F40] border-[#059F40] shadow-md shadow-[#059F40]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3098">
                  <div className={`w-7 h-7 shrink-0 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15'}`} data-id="element-3099">
                    <PIcon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3100" />
                  </div>
                  <div className="flex flex-col" data-id="element-3101">
                    <span className={`font-bold text-xs leading-tight whitespace-nowrap ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3102">
                      {p.name}
                    </span>
                    <span className={`text-[10px] font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3103">
                      {p.typicalYards}
                    </span>
                  </div>
                </Link>;
          })}
          </div>

          {/* sm+ grid */}
          <div className="hidden sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2" data-id="element-3104">
            {projects.map(p => {
            const PIcon = p.icon;
            const isActive = p.slug === project.slug;
            return <Link key={p.slug} to={`/projects/${p.slug}`} className={`group relative flex items-center gap-2.5 px-3 py-2.5 rounded-lg border transition-all ${isActive ? 'bg-[#059F40] dark:bg-[#059F40] border-[#059F40] dark:border-[#059F40] shadow-md shadow-[#059F40]/20' : 'bg-white dark:bg-stone-900/60 border-stone-200 dark:border-white/10 hover:border-[#059F40] dark:hover:border-[#22c95c] hover:shadow-sm'}`} aria-current={isActive ? 'page' : undefined} data-id="element-3105">
                  <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center transition-colors ${isActive ? 'bg-white/20' : 'bg-[#e8ede4] dark:bg-emerald-500/15 group-hover:bg-[#d4ddcf] dark:group-hover:bg-emerald-500/25'}`} data-id="element-3106">
                    <PIcon className={`w-4 h-4 ${isActive ? 'text-white' : 'text-[#3d7530] dark:text-[#9dd194]'}`} data-id="element-3107" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-3108">
                    <div className={`font-bold text-sm leading-tight ${isActive ? 'text-white' : 'text-stone-900 dark:text-white'}`} data-id="element-3109">
                      {p.name}
                    </div>
                    <div className={`text-[10px] mt-0.5 font-medium ${isActive ? 'text-white/80' : 'text-stone-500 dark:text-stone-400'}`} data-id="element-3110">
                      {p.typicalYards}
                    </div>
                  </div>
                </Link>;
          })}
          </div>
        </div>

        {/* Header */}
        <div className="mb-12" data-id="element-3111">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brand-orange/10 text-brand-orange mb-6" data-id="element-3112">
            <Icon className="w-8 h-8" data-id="element-3113" />
          </div>
          <h1 className="text-4xl md:text-5xl font-heading font-black text-stone-900 dark:text-white mb-4" data-id="element-3114">
            {project.name}
          </h1>
          <p className="text-xl md:text-2xl text-stone-600 dark:text-stone-300 font-medium mb-6" data-id="element-3115">
            {project.tagline}
          </p>
          <div className="prose prose-lg dark:prose-invert text-stone-600 dark:text-stone-400 max-w-none" data-id="element-3116">
            <p data-id="element-3117">{project.intro}</p>
          </div>
          <div className="mt-6 inline-flex items-center px-4 py-2 rounded-full bg-stone-100 dark:bg-white/5 text-stone-700 dark:text-stone-300 text-sm font-medium border border-stone-200 dark:border-white/10" data-id="element-3118">
            <span className="mr-2" data-id="element-3119">Typical project size:</span>
            <span className="font-bold text-stone-900 dark:text-white" data-id="element-3120">
              {project.typicalYards}
            </span>
          </div>
        </div>

        {/* Materials Section */}
        <div className="mb-16" data-id="element-3121">
          <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6" data-id="element-3122">
            Recommended Materials
          </h2>
          <div className="space-y-6" data-id="element-3123">
            {project.materials.map((mat, idx) => {
            const product = getProductDetails(mat.slug);
            if (!product) return null;
            return <motion.div key={mat.slug} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="bg-[#ffffff] dark:bg-[#282828] rounded-2xl p-6 shadow-sm border border-stone-200 dark:border-white/10 flex flex-col md:flex-row gap-6 items-start" data-id="element-3124">
                  <div className="w-full md:w-48 h-32 shrink-0 rounded-xl overflow-hidden bg-stone-100 dark:bg-stone-800" data-id="element-3125">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-3126" />
                  </div>
                  <div className="flex-1" data-id="element-3127">
                    <div className="flex flex-wrap items-start justify-between gap-4 mb-2" data-id="element-3128">
                      <div data-id="element-3129">
                        <div className="flex items-center gap-2 mb-1" data-id="element-3130">
                          <span className="text-xs font-bold uppercase tracking-wider text-brand-orange" data-id="element-3131">
                            {mat.role}
                          </span>
                          <span className="text-stone-300 dark:text-stone-600" data-id="element-3132">
                            •
                          </span>
                          <span className="text-xs font-bold uppercase tracking-wider text-stone-500 dark:text-stone-400" data-id="element-3133">
                            Depth: {mat.depth}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-stone-900 dark:text-white" data-id="element-3134">
                          {product.name}
                        </h3>
                      </div>
                      <div className="text-right" data-id="element-3135">
                        <div className="text-lg font-bold text-stone-900 dark:text-white" data-id="element-3136">
                          ${product.pricePerYard}
                          <span className="text-sm font-normal text-stone-500 dark:text-stone-400" data-id="element-3137">
                            /yd
                          </span>
                        </div>
                      </div>
                    </div>

                    <p className="text-stone-600 dark:text-stone-300 mb-4" data-id="element-3138">
                      <span className="font-semibold text-stone-900 dark:text-white" data-id="element-3139">
                        Why:{' '}
                      </span>
                      {mat.why}
                    </p>

                    <Link to={`/shop/${product.slug}`} className="inline-flex items-center text-sm font-bold text-brand-orange hover:text-brand-orange/80 transition-colors" data-id="element-3140">
                      View product details
                      <ArrowRight className="w-4 h-4 ml-1" data-id="element-3141" />
                    </Link>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-12" data-id="element-3142">
          {/* Pro Tips */}
          <div data-id="element-3143">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3144">
              <CheckCircle2 className="w-6 h-6 mr-2 text-[#059F40]" data-id="element-3145" />
              Pro Tips
            </h2>
            <ul className="space-y-4" data-id="element-3146">
              {project.tips.map((tip, idx) => <li key={idx} className="flex items-start" data-id="element-3147">
                  <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-brand-orange mt-2 mr-3" data-id="element-3148" />
                  <span className="text-stone-600 dark:text-stone-300 leading-relaxed" data-id="element-3149">
                    {tip}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* FAQs */}
          <div data-id="element-3150">
            <h2 className="text-2xl font-heading font-bold text-stone-900 dark:text-white mb-6 flex items-center" data-id="element-3151">
              <HelpCircle className="w-6 h-6 mr-2 text-brand-orange" data-id="element-3152" />
              Project FAQs
            </h2>
            <div className="space-y-6" data-id="element-3153">
              {project.faqs.map((faq, idx) => <div key={idx} data-id="element-3154">
                  <h4 className="font-bold text-stone-900 dark:text-white mb-2" data-id="element-3155">
                    {faq.q}
                  </h4>
                  <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed" data-id="element-3156">
                    {faq.a}
                  </p>
                </div>)}
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 rounded-2xl p-8 md:p-12 text-center bg-[#233526]" data-id="element-3157">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4" data-id="element-3158">
            Ready to start your project?
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto" data-id="element-3159">
            Get an instant estimate including delivery for all the materials you
            need.
          </p>
          <Link to={`/?from=project&project=${project.slug}#estimator`} className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-white bg-brand-orange hover:bg-brand-orange/90 rounded-xl transition-colors" data-id="element-3160">
            Get an Estimate
            <ArrowRight className="w-5 h-5 ml-2" data-id="element-3161" />
          </Link>
        </div>
      </div>
    </main>;
}